/**
 * Rolherkenning op één plek.
 *
 * Aanleiding: de rol `OA-medewerker` (#378) is toegevoegd naast `Onderaannemer`.
 * Op tientallen plekken stond `role === 'Onderaannemer'` uitgeschreven, waardoor
 * een medewerker overal door de mazen viel — hij kreeg bijvoorbeeld het interne
 * dashboard, en het communicatie-dashboard rekende hem tot kantoor.
 *
 * Voeg je hier een externe rol toe, dan volgt de rest vanzelf. Schrijf de
 * vergelijking niet opnieuw uit in een component.
 */

/** Rollen die buiten Pracht staan: geen interne gegevens, geen euro's. */
export const EXTERNE_ROLLEN = ['Onderaannemer', 'OA-medewerker', 'Transporteur'] as const;

/** De onderaannemer-kant: het hoofdaccount plus zijn medewerkers. */
export const OA_ROLLEN = ['Onderaannemer', 'OA-medewerker'] as const;

/** Iemand van buiten Pracht — onderaannemer, diens medewerker, of transporteur. */
export function isExterneRol(rol: string | null | undefined): boolean {
  return !!rol && (EXTERNE_ROLLEN as readonly string[]).includes(rol);
}

/** De onderaannemer-kant (hoofdaccount óf medewerker). Krijgt het OA-portaal. */
export function isOAKant(rol: string | null | undefined): boolean {
  return !!rol && (OA_ROLLEN as readonly string[]).includes(rol);
}

/**
 * Het hoofdaccount van een onderaannemer. Alleen deze mag contracten, termijnen
 * en bedragen zien, ploegen beheren, beschikbaarheid doorgeven en uren
 * bevestigen — een medewerker niet.
 */
export function isOAHoofdaccount(rol: string | null | undefined): boolean {
  return rol === 'Onderaannemer';
}

/** Een medewerker van een onderaannemer: uitvoerend, zonder financieel zicht. */
export function isOAMedewerker(rol: string | null | undefined): boolean {
  return rol === 'OA-medewerker';
}

/** Intern personeel van Pracht. Spiegelbeeld van `isExterneRol`. */
export function isInterneRol(rol: string | null | undefined): boolean {
  return !!rol && !isExterneRol(rol);
}

/**
 * Interne kantoorrol: intern én geen (gewone) Adviseur. Spiegel van de
 * databasefunctie `is_intern_kantoor()`; de hoofd adviseur valt er bewust wél
 * onder. Voor interne werkaansturing (taken aan anderen geven, overleg).
 */
export function isInternKantoor(rol: string | null | undefined): boolean {
  return isInterneRol(rol) && rol !== 'Adviseur';
}

/**
 * De adviseurskant: gewone adviseurs plus de hoofd adviseur, die zelf ook
 * adviseert. Gebruik deze lijst overal waar "alle adviseurs" wordt opgehaald
 * (afspraak-planning, KPI's, toewijs-dropdowns) — met `role = 'Adviseur'`
 * verdwijnt de hoofd adviseur uit die lijsten.
 */
export const ADVISEUR_ROLLEN = ['Adviseur', 'Hoofd Adviseur'] as const;

/** Adviseert zelf (gewoon of als hoofd): opnames, offertes, eigen pipeline. */
export function isAdviseurRol(rol: string | null | undefined): boolean {
  return !!rol && (ADVISEUR_ROLLEN as readonly string[]).includes(rol);
}

/**
 * De hoofd adviseur (besluit 29-08-2026): ziet en bewaakt alle adviseurs,
 * wijst leads toe en leest adviseursmail. Sinds besluit Tom 01-10-2026 heeft
 * de rol daarbovenop overal de rechten van Werkvoorbereider (migratie
 * 20261002063000): zet hem dus in elke rollenlijst waar Werkvoorbereider staat.
 * Wat Admin-only is (tarieven, prijslijsten, meldingsregels) blijft dicht.
 */
export function isHoofdAdviseur(rol: string | null | undefined): boolean {
  return rol === 'Hoofd Adviseur';
}

/**
 * Inbox-triage: de hele inbox zien, mails en taken van anderen toewijzen en
 * doorgeven. Secretaresse en Admin, plus sinds besluit Tom 27-10-2026 de hoofd
 * adviseur (migratie 20261028180000). De RLS is de harde poort.
 */
export const INBOX_BEHEER_ROLLEN = ['Admin', 'Secretaresse', 'Hoofd Adviseur'] as const;

export function isInboxBeheerder(rol: string | null | undefined): boolean {
  return !!rol && (INBOX_BEHEER_ROLLEN as readonly string[]).includes(rol);
}
